/**
 * Haushalts-Essensplan (27.10.2026) — **lokal zuerst**, wie alles andere.
 *
 * - Der Plan (`household_meals`) gehört dem Haushalt; das Gericht wird beim
 *   Eintragen eingefroren (`dishFromRecipe`) — kein Verweis auf ein Rezept.
 * - „Wer isst mit“ (`household_meal_eaters`) braucht der Haushalt für den
 *   Einkauf. Für mich schreibt sie mein Gerät aus meiner Abweichung und
 *   meinen Dauerregeln, für Kinder die Erwachsenen.
 * - Meine Abweichung (`meal_deviations`) bleibt privat. Aus ihr entstehen
 *   meine Protokollzeilen (`householdLogId`): Geplant zählt als gegessen,
 *   ab heute, nie rückwirkend, nie doppelt zu Eingetragenem; ausgelassen
 *   heißt weich gelöscht, und das bleibt es.
 *
 * Die Rechenregeln stehen in `@kraftwerk/shared` (`householdMeals.ts`); hier
 * wird nur gelesen und geschrieben. Lesefunktionen bekommen den Haushalt
 * hineingereicht (Live-Abfragen lesen flach).
 */

import {
  DEFAULT_SHARE,
  HOUSEHOLD_MEAL_HORIZON_DAYS,
  addDays,
  cleanNutrients,
  deviationId,
  dishLogValues,
  eaterRowId,
  eaterShare,
  eaterWriteRule,
  householdExtraLogId,
  householdLogId,
  householdNeeds,
  isNoDeviation,
  isSameMeal,
  macrosForAmount,
  mealRules,
  mealRulesId,
  namedDish,
  newId,
  nutrientsForAmount,
  readDeviation,
  readShare,
  roundMacros,
  sameShare,
  scaleMacros,
  scaleNutrients,
  servingFor,
  trainingExtraLogId,
} from '@kraftwerk/shared';
import type {
  DaySlot,
  EaterRow,
  EaterShare,
  FrozenDish,
  HouseholdNeeds,
  MealDeviation,
  MealExtra,
  MealRules,
  Serving,
} from '@kraftwerk/shared';
import { db } from './local.js';
import type {
  KraftwerkDatabase,
  LocalFood,
  LocalFoodLogEntry,
  LocalHouseholdMeal,
  LocalHouseholdMealEater,
  LocalHouseholdMember,
  LocalMealDeviation,
  LocalRecipe,
} from './local.js';
import { listIngredients, macrosOfRecipe, nutrientsOfRecipe } from './nutrition.js';
import { stamp, writeRows } from './write.js';
import type { RowChange } from './write.js';

/** Wer ich bin und ob meine Bilanz zählt (Modul Ernährung). */
export interface MealContext {
  me: string;
  /** Ernährung an (E15; bei Kindern nur über `CHILD_MODULES`) — sonst nur Anzeige. */
  nutritionOn: boolean;
  today: string;
}

// --- Lesen -------------------------------------------------------------------

export async function listHouseholdMeals(
  householdId: string | null,
  from: string,
  to: string,
  database: KraftwerkDatabase = db,
): Promise<LocalHouseholdMeal[]> {
  if (!householdId) return [];
  const rows = await database.household_meals.where('householdId').equals(householdId).toArray();
  return rows
    .filter(
      (row) =>
        !row.deletedAt &&
        row.status === 'planned' &&
        row.date !== null &&
        row.date >= from &&
        row.date <= to,
    )
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
}

export async function listWishes(
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalHouseholdMeal[]> {
  if (!householdId) return [];
  const rows = await database.household_meals.where('householdId').equals(householdId).toArray();
  return rows
    .filter((row) => !row.deletedAt && row.status === 'wish')
    .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt));
}

export async function listEaters(
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalHouseholdMealEater[]> {
  if (!householdId) return [];
  return database.household_meal_eaters.where('householdId').equals(householdId).toArray();
}

/** Aktive Mitglieder — wer ab Werk mitisst. */
export async function activeMembers(
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalHouseholdMember[]> {
  if (!householdId) return [];
  const rows = await database.household_members.where('householdId').equals(householdId).toArray();
  return rows
    .filter((row) => !row.deletedAt)
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'));
}

export async function readRules(database: KraftwerkDatabase = db, me: string): Promise<MealRules> {
  const row = await database.meal_deviations.get(mealRulesId(me));
  return mealRules(row && !row.deletedAt ? row.rules : null);
}

export async function readOwnDeviation(
  entryId: string,
  me: string,
  database: KraftwerkDatabase = db,
): Promise<MealDeviation | null> {
  const row = await database.meal_deviations.get(deviationId(me, entryId));
  return row && !row.deletedAt ? readDeviation(row) : null;
}

function eaterRowsOf(rows: readonly LocalHouseholdMealEater[]): EaterRow[] {
  return rows.map((row) => ({ ...row }));
}

// --- Gericht einfrieren -------------------------------------------------------

/**
 * Ein eigenes Gericht für den Haushalt einfrieren: Name, Portionen-Bezug,
 * Nährwerte je Portion (gerechnet oder eingetragen — nie erfunden; ohne
 * Werte `null`), Zutaten mit Name, Menge und Regal. Freie Zeilen bleiben
 * ohne Menge. Danach ist der Eintrag vom Rezept unabhängig.
 */
export async function dishFromRecipe(
  recipe: LocalRecipe,
  database: KraftwerkDatabase = db,
): Promise<FrozenDish> {
  const perPortion = await macrosOfRecipe(recipe, database);
  const nutrients = await nutrientsOfRecipe(recipe, database);
  const ingredients = await listIngredients(recipe.id, database);
  const foods = await database.foods.bulkGet(
    ingredients.map((entry) => entry.foodId ?? '').filter(Boolean),
  );
  const foodById = new Map(
    foods.filter((food): food is LocalFood => Boolean(food)).map((food) => [food.id, food]),
  );
  const lines = ingredients.flatMap((entry) => {
    if (entry.foodId === null) {
      const label = entry.label?.trim();
      return label ? [{ name: label, grams: null, category: 'other' as const }] : [];
    }
    const food = foodById.get(entry.foodId);
    if (!food || food.deletedAt) return [];
    return [{ name: food.name.trim(), grams: entry.grams, category: food.category ?? 'other' }];
  });
  const fromIngredients = recipe.macroSource === 'ingredients';
  const sources = fromIngredients
    ? [...new Set([...foodById.values()].map((food) => food.sourceNote?.trim()).filter(Boolean))]
    : [];
  return {
    name: recipe.name.trim(),
    portions: recipe.portions > 0 ? recipe.portions : 1,
    perPortion: perPortion ? roundMacros(perPortion) : null,
    nutrients: cleanNutrients(nutrients),
    sourceNote: recipe.sourceNote?.trim() || (sources.length > 0 ? sources.join('; ') : null),
    ingredients: lines,
    items: lines.length === 0 ? recipe.items : null,
  };
}

/** Extra aus einem eigenen Lebensmittel — Werte für genau diese Menge, mit Quelle. */
export function extraFromFood(food: LocalFood, grams: number): MealExtra {
  const per100g = {
    kcal: food.kcalPer100g,
    protein: food.proteinPer100g,
    carbs: food.carbsPer100g,
    fat: food.fatPer100g,
  };
  return {
    id: newId(),
    name: food.name.trim(),
    grams,
    portions: null,
    macros: roundMacros(macrosForAmount(per100g, grams)),
    nutrients: nutrientsForAmount(food.nutrients, grams),
    sourceNote: food.sourceNote,
    category: food.category ?? 'other',
  };
}

/** Extra aus einem eigenen Gericht — `null`, wenn es keine Nährwerte hat. */
export async function extraFromRecipe(
  recipe: LocalRecipe,
  portions: number,
  database: KraftwerkDatabase = db,
): Promise<MealExtra | null> {
  const perPortion = await macrosOfRecipe(recipe, database);
  if (!perPortion) return null;
  const nutrients = await nutrientsOfRecipe(recipe, database);
  return {
    id: newId(),
    name: recipe.name.trim(),
    grams: null,
    portions,
    macros: roundMacros(scaleMacros(perPortion, portions)),
    nutrients: scaleNutrients(nutrients, portions),
    sourceNote: recipe.sourceNote,
    category: 'other',
  };
}

// --- Plan und Wünsche (Haushalt) ---------------------------------------------

export interface MealDraft {
  householdId: string;
  date: string;
  slot: DaySlot;
  dish: FrozenDish;
  note?: string | null;
  cookId?: string | null;
}

export async function planHouseholdMeal(
  draft: MealDraft,
  database: KraftwerkDatabase = db,
): Promise<LocalHouseholdMeal> {
  const row: LocalHouseholdMeal = {
    id: newId(),
    updatedAt: stamp(),
    deletedAt: null,
    householdId: draft.householdId,
    status: 'planned',
    date: draft.date,
    slot: draft.slot,
    dish: draft.dish,
    note: draft.note?.trim() || null,
    cookId: draft.cookId ?? null,
  };
  await writeRows([{ table: 'household_meals', row }], database);
  return row;
}

export async function updateHouseholdMeal(
  id: string,
  changes: Partial<Pick<LocalHouseholdMeal, 'date' | 'slot' | 'dish' | 'note' | 'cookId'>>,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.household_meals.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRows(
    [{ table: 'household_meals', row: { ...existing, ...changes, updatedAt: stamp() } }],
    database,
  );
}

/** Eintrag oder Wunsch löschen (weich — die anderen Geräte erfahren es). */
export async function removeHouseholdMeal(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.household_meals.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRows(
    [
      {
        table: 'household_meals',
        row: { ...existing, deletedAt: now, updatedAt: now },
        kind: 'delete',
      },
    ],
    database,
  );
}

/** Ein Wunsch — ab Stufe `school` auch von Kindern. Tag und Mahlzeit sind freiwillig. */
export async function addWish(
  input: {
    householdId: string;
    name: string;
    note?: string | null;
    date?: string | null;
    slot?: DaySlot | null;
  },
  database: KraftwerkDatabase = db,
): Promise<LocalHouseholdMeal | null> {
  const dish = namedDish(input.name);
  if (!dish.name) return null;
  const row: LocalHouseholdMeal = {
    id: newId(),
    updatedAt: stamp(),
    deletedAt: null,
    householdId: input.householdId,
    status: 'wish',
    date: input.date ?? null,
    slot: input.slot ?? null,
    dish,
    note: input.note?.trim() || null,
    cookId: null,
  };
  await writeRows([{ table: 'household_meals', row }], database);
  return row;
}

/**
 * Einen Wunsch in den Plan übernehmen (Erwachsene): dieselbe Zeile wird zum
 * Eintrag — wer sich etwas gewünscht hat, sieht es im Plan. Mit einem
 * eigenen Gericht bekommt der Eintrag dessen Werte und Zutaten.
 */
export async function takeWish(
  id: string,
  target: { date: string; slot: DaySlot; dish?: FrozenDish | null },
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  const existing = await database.household_meals.get(id);
  if (!existing || existing.deletedAt || existing.status !== 'wish') return false;
  await writeRows(
    [
      {
        table: 'household_meals',
        row: {
          ...existing,
          status: 'planned',
          date: target.date,
          slot: target.slot,
          dish: target.dish ?? existing.dish,
          updatedAt: stamp(),
        },
      },
    ],
    database,
  );
  return true;
}

// --- Wer isst mit (für Kinder, von Erwachsenen) ------------------------------

function eaterRow(
  entry: Pick<LocalHouseholdMeal, 'id' | 'householdId'>,
  eaterId: string,
  share: EaterShare,
  existing: LocalHouseholdMealEater | undefined,
): LocalHouseholdMealEater {
  return {
    ...(existing ?? {}),
    id: eaterRowId(entry.id, eaterId),
    updatedAt: stamp(),
    deletedAt: null,
    householdId: entry.householdId,
    entryId: entry.id,
    eaterId,
    portions: share.portions,
    items: share.items,
    variant: share.variant,
    vegetarian: share.vegetarian,
  };
}

/**
 * Die Mitess-Zeile setzen: ab Werk (eine Portion, nichts dazu) heißt weich
 * gelöscht — keine Zeile ist der Stand ab Werk.
 */
function eaterChange(
  entry: Pick<LocalHouseholdMeal, 'id' | 'householdId'>,
  eaterId: string,
  share: EaterShare,
  existing: LocalHouseholdMealEater | undefined,
): RowChange | null {
  const isDefault = sameShare(share, DEFAULT_SHARE);
  if (isDefault) {
    if (!existing || existing.deletedAt) return null;
    const now = stamp();
    return {
      table: 'household_meal_eaters',
      row: { ...existing, deletedAt: now, updatedAt: now },
      kind: 'delete',
    };
  }
  if (existing && !existing.deletedAt && sameShare(readShare(existing), share)) return null;
  return { table: 'household_meal_eaters', row: eaterRow(entry, eaterId, share, existing) };
}

/**
 * Für ein Kind (Erwachsene): „isst nicht mit“ oder eine andere Portion. Für
 * mich selbst geht es über `saveDeviation` — dort hängen auch Bilanz und
 * Extras dran.
 */
export async function setChildPortions(
  entry: LocalHouseholdMeal,
  childId: string,
  portions: number,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.household_meal_eaters.get(eaterRowId(entry.id, childId));
  const current = existing && !existing.deletedAt ? readShare(existing) : DEFAULT_SHARE;
  const change = eaterChange(entry, childId, { ...current, portions }, existing);
  if (change) await writeRows([change], database);
}

/** Darf ich die Mitess-Zeile schreiben? Dieselbe Regel wie beim Server. */
export function mayWriteEater(
  me: LocalHouseholdMember | undefined,
  eater: LocalHouseholdMember | undefined,
): boolean {
  if (!me) return false;
  return (
    eaterWriteRule(
      { role: me.role ?? 'adult', level: me.role === 'child' ? asLevel(me.childLevel) : null },
      me.userId,
      eater ? { userId: eater.userId, role: eater.role ?? 'adult' } : null,
    ) === null
  );
}

function asLevel(stored: string | null | undefined) {
  return stored === 'small' || stored === 'teen' ? stored : 'school';
}

// --- Meine Abweichung und meine Bilanz ----------------------------------------

/** Was ich an einem Eintrag esse — mit Abweichung und Dauerregeln. */
export function myServing(
  entry: LocalHouseholdMeal,
  deviation: MealDeviation | null,
  rules: MealRules,
): Serving | null {
  if (!entry.slot) return null;
  return servingFor({ slot: entry.slot, dish: entry.dish }, deviation, rules);
}

function logRow(
  id: string,
  entry: { date: string; slot: DaySlot },
  label: string,
  values: {
    macros: { kcal: number; protein: number; carbs: number; fat: number };
    nutrients: object;
  },
  amount: { portions: number | null; grams: number | null },
  existing: LocalFoodLogEntry | undefined,
): LocalFoodLogEntry {
  const nutrients = cleanNutrients(values.nutrients);
  const now = stamp();
  return {
    id,
    updatedAt: now,
    deletedAt: null,
    date: entry.date,
    slot: entry.slot,
    eatenAt: existing?.eatenAt ?? now,
    recipeId: null,
    foodId: null,
    label,
    portions: amount.portions,
    grams: amount.grams,
    ...roundMacros(values.macros),
    nutrients: Object.keys(nutrients).length > 0 ? nutrients : null,
    note: null,
    estimate: null,
  };
}

function sameLog(row: LocalFoodLogEntry, next: LocalFoodLogEntry): boolean {
  return (
    row.label === next.label &&
    row.kcal === next.kcal &&
    row.protein === next.protein &&
    row.carbs === next.carbs &&
    row.fat === next.fat &&
    row.portions === next.portions &&
    row.grams === next.grams &&
    JSON.stringify(row.nutrients ?? {}) === JSON.stringify(next.nutrients ?? {})
  );
}

interface LogPlan {
  /** Bewusste Änderung (Knopf): auch heute neu schreiben und Ausgelassenes zurückholen. */
  explicit: boolean;
  /** Extras, die es nicht mehr gibt — ihre Zeilen gehen. */
  removedExtras: readonly string[];
}

/**
 * Meine Protokollzeilen für einen Eintrag — als Änderungen, nicht geschrieben.
 *
 * - nur ab heute und nur mit Modul Ernährung (sonst nur Anzeige);
 * - „nicht mit“ oder gelöschter Eintrag: lebende Zeilen gehen (weich);
 * - fehlt die Zeile, entsteht sie — nicht, wenn die Mahlzeit ausgelassen
 *   ist oder dasselbe Gericht schon von Hand eingetragen wurde;
 * - eine weich gelöschte Zeile ist ausgelassen und bleibt es, außer bei
 *   einer bewussten Änderung;
 * - künftige Tage ziehen Änderungen nach, heute nur bei bewusster Änderung;
 * - ohne Nährwerte keine Zeile (nie still 0 kcal).
 */
function logChanges(
  entry: LocalHouseholdMeal,
  serving: Serving | null,
  rows: ReadonlyMap<string, LocalFoodLogEntry>,
  dayRows: readonly LocalFoodLogEntry[],
  skippedSlots: ReadonlySet<string>,
  context: MealContext,
  plan: LogPlan,
): RowChange[] {
  if (!context.nutritionOn || !entry.date || !entry.slot || entry.date < context.today) return [];
  const target = { date: entry.date, slot: entry.slot };
  const changes: RowChange[] = [];
  const remove = (id: string) => {
    const existing = rows.get(id);
    if (!existing || existing.deletedAt) return;
    const now = stamp();
    changes.push({
      table: 'food_log',
      row: { ...existing, deletedAt: now, updatedAt: now },
      kind: 'delete',
    });
  };
  const mainId = householdLogId(context.me, entry.id);
  for (const extraId of plan.removedExtras)
    remove(householdExtraLogId(context.me, entry.id, extraId));
  if (entry.deletedAt || !serving || !serving.eats) {
    remove(mainId);
    for (const extra of serving?.extras ?? []) {
      remove(householdExtraLogId(context.me, entry.id, extra.id));
    }
    return changes;
  }

  const desired: { id: string; row: LocalFoodLogEntry | null }[] = [];
  const values = dishLogValues(serving.dish, serving.portions);
  desired.push({
    id: mainId,
    row: values
      ? logRow(
          mainId,
          target,
          serving.dish.name,
          values,
          { portions: serving.portions, grams: null },
          rows.get(mainId),
        )
      : null,
  });
  for (const extra of serving.extras) {
    const id = householdExtraLogId(context.me, entry.id, extra.id);
    desired.push({
      id,
      row: logRow(
        id,
        target,
        extra.name,
        { macros: extra.macros, nutrients: extra.nutrients },
        { portions: extra.portions, grams: extra.grams },
        rows.get(id),
      ),
    });
  }

  for (const { id, row } of desired) {
    const existing = rows.get(id);
    if (!row) {
      // Ohne Nährwerte (etwa eine Variante ohne Werte): nichts zählt still mit.
      remove(id);
      continue;
    }
    if (!existing) {
      if (skippedSlots.has(entry.slot)) continue;
      if (
        !plan.explicit &&
        dayRows.some((other) => other.id !== id && isSameMeal(other, entry.slot!, row.label))
      ) {
        continue;
      }
      changes.push({ table: 'food_log', row });
      continue;
    }
    if (existing.deletedAt && !plan.explicit) continue;
    if (!existing.deletedAt && entry.date === context.today && !plan.explicit) continue;
    if (!existing.deletedAt && sameLog(existing, row)) continue;
    changes.push({ table: 'food_log', row });
  }
  return changes;
}

/** Zeilen eines Tages, deren Kennungen zu einem Eintrag gehören könnten. */
async function dayLog(date: string, database: KraftwerkDatabase): Promise<LocalFoodLogEntry[]> {
  return database.food_log.where('date').equals(date).toArray();
}

async function skippedSlotsOf(date: string, database: KraftwerkDatabase): Promise<Set<string>> {
  const skips = await database.meal_skips.where('date').equals(date).toArray();
  return new Set(skips.filter((row) => !row.deletedAt).map((row) => row.slot));
}

/** Mitess-Zeile aus meiner Abweichung — `null`, wenn nichts zu schreiben ist. */
function myEaterChange(
  entry: LocalHouseholdMeal,
  serving: Serving | null,
  existing: LocalHouseholdMealEater | undefined,
  context: MealContext,
  force: boolean,
  hasOwnSay: boolean,
): RowChange | null {
  if (!serving || entry.deletedAt) return null;
  // Ohne eigene Abweichung und ohne Dauerregel, die hier greift: nicht
  // anfassen — die Zeile kann von den Eltern stammen (Kind) oder aus dem
  // Reiter Essen. Nur eine bewusste Änderung setzt zurück.
  if (!hasOwnSay && !force) return null;
  return eaterChange(entry, context.me, eaterShare(serving), existing);
}

function ruleTouches(rules: MealRules, slot: DaySlot | null): boolean {
  return slot !== null && (rules.factors[slot] !== undefined || rules.vegetarian);
}

/**
 * Meine Abweichung an einem Eintrag speichern — mit Mitess-Zeile für den
 * Haushalt und meinen Protokollzeilen in **einem** `writeRows`. Keine
 * Abweichung mehr heißt: Zeile weich gelöscht, ich esse wieder wie der
 * Haushalt.
 */
export async function saveDeviation(
  entry: LocalHouseholdMeal,
  next: MealDeviation,
  context: MealContext,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const id = deviationId(context.me, entry.id);
  const [stored, rules, eater, rowsOfDay, skipped] = await Promise.all([
    database.meal_deviations.get(id),
    readRules(database, context.me),
    database.household_meal_eaters.get(eaterRowId(entry.id, context.me)),
    entry.date ? dayLog(entry.date, database) : Promise.resolve([]),
    entry.date ? skippedSlotsOf(entry.date, database) : Promise.resolve(new Set<string>()),
  ]);
  const previous = stored && !stored.deletedAt ? readDeviation(stored) : null;
  const changes: RowChange[] = [];
  const now = stamp();
  if (isNoDeviation(next)) {
    if (stored && !stored.deletedAt) {
      changes.push({
        table: 'meal_deviations',
        row: { ...stored, ...EMPTY_DEVIATION_FIELDS, deletedAt: now, updatedAt: now },
        kind: 'delete',
      });
    }
  } else {
    const row: LocalMealDeviation = {
      id,
      updatedAt: now,
      deletedAt: null,
      entryId: entry.id,
      skip: next.skip,
      factor: next.factor,
      extras: next.extras,
      variant: next.variant,
      rules: null,
    };
    changes.push({ table: 'meal_deviations', row });
  }
  const serving = myServing(entry, isNoDeviation(next) ? null : next, rules);
  const eaterUpdate = myEaterChange(entry, serving, eater, context, true, true);
  if (eaterUpdate) changes.push(eaterUpdate);
  const keptExtras = new Set(next.extras.map((extra) => extra.id));
  changes.push(
    ...logChanges(
      entry,
      serving,
      new Map(rowsOfDay.map((row) => [row.id, row])),
      rowsOfDay,
      skipped,
      context,
      {
        explicit: true,
        removedExtras: (previous?.extras ?? [])
          .map((extra) => extra.id)
          .filter((extraId) => !keptExtras.has(extraId)),
      },
    ),
  );
  await writeRows(changes, database);
}

const EMPTY_DEVIATION_FIELDS = { skip: false, factor: null, extras: [], variant: null };

/**
 * Dauerregeln speichern und auf die kommenden Einträge anwenden: Mitess-
 * Zeilen für den Einkauf und meine Protokollzeilen der nächsten Tage.
 */
export async function saveMealRules(
  householdId: string | null,
  rules: MealRules,
  context: MealContext,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const id = mealRulesId(context.me);
  const row: LocalMealDeviation = {
    id,
    updatedAt: stamp(),
    deletedAt: null,
    entryId: null,
    skip: false,
    factor: null,
    extras: [],
    variant: null,
    rules,
  };
  await writeRows([{ table: 'meal_deviations', row }], database);
  await syncHouseholdMeals(householdId, context, database, { rulesChanged: true });
}

/**
 * Abgleich für die kommenden Tage — beim Öffnen von Tag und Reiter Essen:
 *
 * - meine Protokollzeilen (Geplant zählt als gegessen, ab heute);
 * - meine Mitess-Zeilen, wo eine Abweichung oder Dauerregel greift;
 * - das Extra an Trainingstagen (`training`: Tage mit geplantem Training aus
 *   der vorhandenen Trainingslogik — hier wird nichts neu gerechnet).
 */
export async function syncHouseholdMeals(
  householdId: string | null,
  context: MealContext,
  database: KraftwerkDatabase = db,
  options: {
    /** Tage, für die das Trainings-Extra geprüft wird, und welche davon Trainingstage sind. */
    training?: { dates: readonly string[]; on: ReadonlySet<string> };
    rulesChanged?: boolean;
  } = {},
): Promise<void> {
  const to = addDays(context.today, HOUSEHOLD_MEAL_HORIZON_DAYS);
  const [entries, rules, eaters, deviations] = await Promise.all([
    householdId
      ? database.household_meals.where('householdId').equals(householdId).toArray()
      : Promise.resolve([] as LocalHouseholdMeal[]),
    readRules(database, context.me),
    listEaters(householdId, database),
    database.meal_deviations.toArray(),
  ]);
  const deviationById = new Map(deviations.map((row) => [row.id, row]));
  const eaterById = new Map(eaters.map((row) => [row.id, row]));
  const upcoming = entries.filter(
    (entry) =>
      entry.status === 'planned' &&
      entry.date !== null &&
      entry.date >= context.today &&
      entry.date <= to,
  );
  const dates = [...new Set(upcoming.map((entry) => entry.date!))];
  const byDate = new Map<string, LocalFoodLogEntry[]>();
  const skippedByDate = new Map<string, Set<string>>();
  for (const date of new Set([...dates, ...(options.training?.dates ?? [])])) {
    byDate.set(date, await dayLog(date, database));
    skippedByDate.set(date, await skippedSlotsOf(date, database));
  }

  const changes: RowChange[] = [];
  for (const entry of upcoming) {
    const stored = deviationById.get(deviationId(context.me, entry.id));
    const deviation = stored && !stored.deletedAt ? readDeviation(stored) : null;
    const serving = myServing(entry, deviation, rules);
    const hasOwnSay = deviation !== null || ruleTouches(rules, entry.slot);
    const eaterUpdate = myEaterChange(
      entry,
      serving,
      eaterById.get(eaterRowId(entry.id, context.me)),
      context,
      false,
      // Eben die Dauerregeln geändert: meine Zeilen folgen ihnen, auch zurück.
      hasOwnSay || Boolean(options.rulesChanged),
    );
    if (eaterUpdate) changes.push(eaterUpdate);
    const rows = byDate.get(entry.date!) ?? [];
    changes.push(
      ...logChanges(
        entry,
        entry.deletedAt ? null : serving,
        new Map(rows.map((row) => [row.id, row])),
        rows,
        skippedByDate.get(entry.date!) ?? new Set(),
        context,
        { explicit: false, removedExtras: [] },
      ),
    );
  }

  // Extra an Trainingstagen — Post-Workout, nur mit Modul Ernährung. Welche
  // Tage Trainingstage sind, sagt die vorhandene Trainingslogik (der Aufrufer
  // reicht `upcomingTraining` herein); hier wird nichts neu gerechnet.
  if (context.nutritionOn && options.training) {
    const extra = rules.trainingExtra;
    for (const date of options.training.dates) {
      if (date < context.today) continue;
      const rows = byDate.get(date) ?? [];
      const id = trainingExtraLogId(context.me, date);
      const existing = rows.find((row) => row.id === id);
      if (!extra || !options.training.on.has(date)) {
        if (existing && !existing.deletedAt) {
          const now = stamp();
          changes.push({
            table: 'food_log',
            row: { ...existing, deletedAt: now, updatedAt: now },
            kind: 'delete',
          });
        }
        continue;
      }
      if (existing?.deletedAt || skippedByDate.get(date)?.has('post')) continue;
      const row = logRow(
        id,
        { date, slot: 'post' },
        extra.name,
        { macros: extra.macros, nutrients: extra.nutrients },
        { portions: extra.portions, grams: extra.grams },
        existing,
      );
      if (existing && (date === context.today || sameLog(existing, row))) continue;
      changes.push({ table: 'food_log', row });
    }
  }
  await writeRows(changes, database);
}

// --- Einkauf -----------------------------------------------------------------

/**
 * Bedarf aus dem Haushaltsplan von `from` bis `to`: Zutaten × Portionen
 * aller Mitesser (Nicht-Mitesser zählen nicht) plus Extras und Varianten.
 * Direkt abgefragt — Live-Abfragen lesen flach.
 */
export async function householdMealNeeds(
  householdId: string | null,
  from: string,
  to: string,
  database: KraftwerkDatabase = db,
): Promise<HouseholdNeeds> {
  if (!householdId) return { lines: [], unstructured: [] };
  const [entries, eaters, members] = await Promise.all([
    database.household_meals.where('householdId').equals(householdId).toArray(),
    database.household_meal_eaters.where('householdId').equals(householdId).toArray(),
    database.household_members.where('householdId').equals(householdId).toArray(),
  ]);
  return householdNeeds(
    entries
      .filter(
        (entry) =>
          !entry.deletedAt &&
          entry.status === 'planned' &&
          entry.date !== null &&
          entry.date >= from &&
          entry.date <= to,
      )
      .map((entry) => ({ id: entry.id, date: entry.date!, dish: entry.dish })),
    members.filter((member) => !member.deletedAt).map((member) => member.userId),
    eaterRowsOf(eaters),
  );
}
